import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { WEBHOOKS } from "../lib/webhooks";

export function PaymentSuccess({ onCreditsGranted, onDone }) {
  const [state, setState] = useState("checking");
  const [credits, setCredits] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {

    const checkCredits = async () => {
      if (doneRef.current) return;

      try {
        const token = localStorage.getItem("token");

        const res = await fetch(WEBHOOKS.me, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        const data = await res.json();

        setAttempts((a) => a + 1);

        if (data.credits > 0) {
          doneRef.current = true;

          setCredits(data.credits);
          setState("paid");

          onCreditsGranted?.(data.credits);
          return;
        }

      } catch (e) {
        console.log(e);
      }

      setAttempts((a) => {
        if (a + 1 >= 10) {
          setState("timeout");
          return a + 1;
        }

        timerRef.current = setTimeout(checkCredits, 2000);
        return a + 1;
      });
    };

    checkCredits();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      doneRef.current = true;
    };

  }, []);

  const goHome = () => {
    window.history.replaceState(null, "", "/");
    onDone?.();
  };

  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="card-tech scanline rounded-2xl p-10 md:p-14 max-w-xl w-full text-center relative overflow-hidden"
      >

        <div className="relative flex flex-col items-center text-center">

          <span 
            className="tag mb-8 inline-flex"
            style={{ 
              borderColor: "rgba(92,225,230,0.3)", 
              color: "rgb(var(--brand))" 
            }}
          >
            PAYMENT · STRIPE
          </span>


          {state === "checking" && (
            <>
              <div className="
                w-20 h-20
                rounded-full
                border
                border-[rgb(var(--brand))]/40
                flex
                items-center
                justify-center
                mb-7
              ">
                <div className="
                  w-7 h-7
                  border-2
                  border-[rgb(var(--brand))]
                  border-t-transparent
                  rounded-full
                  animate-spin
                "/>
              </div>


              <h1 className="
                font-display
                font-light
                text-4xl
                md:text-5xl
                text-[rgb(var(--paper))]
              ">
                Confirmando <span className="italic font-serif text-[rgb(var(--brand))]">
                  tu pago…
                </span>
              </h1>


              <p className="mt-5 text-[rgb(var(--paper-dim))]">
                Sincronizando con Stripe ({attempts}/10).
              </p>


              <div className="
                mt-7
                w-full
                h-1
                rounded-full
                bg-white/5
                overflow-hidden
              ">
                <div
                  className="
                    h-full
                    bg-[rgb(var(--brand))]
                    glow-cyan
                    transition-all
                  "
                  style={{
                    width: `${Math.min(100, attempts * 10)}%`
                  }}
                />
              </div>
            </>
          )}



          {state === "paid" && (
            <>

              {/* CHECK */}
              <div className="
                w-24
                h-24
                rounded-full
                border
                border-[rgb(var(--brand))]/50
                bg-[rgb(var(--brand))]/5
                flex
                items-center
                justify-center
                mb-8
                shadow-[0_0_50px_rgba(92,225,230,0.25)]
              ">
                <svg
                  width="42"
                  height="42"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-[rgb(var(--brand))]"
                >
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              </div>


              <h1 className="
                font-display
                font-light
                text-5xl
                md:text-6xl
                leading-tight
                text-[rgb(var(--paper))]
              ">
                Pago{" "}
                <span className="
                  italic
                  font-serif
                  text-[rgb(var(--brand))]
                ">
                  confirmado.
                </span>
              </h1>


              <p className="
                mt-5
                text-sm
                text-[rgb(var(--paper-dim))]
              ">
                Tu cuenta ha sido actualizada correctamente.
              </p>



              {/* CREDIT CARD */}
              <div className="
                mt-8
                w-full
                max-w-xs
                rounded-2xl
                border
                border-white/10
                bg-white/[0.03]
                py-7
                px-10
              ">

                <div className="
                  text-[15px]
                  uppercase
                  tracking-[0.3em]
                  font-mono
                  text-[rgb(var(--paper-dim))]
                ">
                  Créditos totales
                </div>


                <div className="
                  mt-2
                  font-display
                  italic
                  text-5xl
                  text-[rgb(var(--brand))]
                ">
                  {credits}
                </div>

              </div>



              <button
                onClick={goHome}
                className="
                  mt-9
                  rounded-full
                  bg-[rgb(var(--brand))]
                  text-[rgb(var(--ink))]
                  px-10
                  py-3.5
                  text-sm
                  font-medium
                  hover:bg-[rgb(var(--paper))]
                  transition-all
                "
                data-testid="go-home-btn"
              >
                Abrir el studio →
              </button>


            </>
          )}



          {state === "failed" && (
            <>
              <h1 className="
                font-display
                font-light
                text-5xl
                text-[rgb(var(--paper))]
              ">
                Algo <span className="italic font-serif">
                  falló.
                </span>
              </h1>

              <p className="mt-5 text-[rgb(var(--paper-dim))]">
                No pudimos confirmar el pago.
              </p>

              <button
                onClick={goHome}
                className="
                  mt-8
                  rounded-full
                  border
                  border-white/10
                  px-8
                  py-3
                  text-[rgb(var(--paper))]
                "
              >
                Volver
              </button>
            </>
          )}



          {state === "timeout" && (
            <>
              <h1 className="
                font-display
                font-light
                text-5xl
                text-[rgb(var(--paper))]
              ">
                Tarda <span className="italic font-serif">
                  un poco…
                </span>
              </h1>

              <p className="mt-5 text-[rgb(var(--paper-dim))]">
                El pago sigue procesándose.
              </p>

              <button
                onClick={goHome}
                className="
                  mt-8
                  rounded-full
                  border
                  border-white/10
                  px-8
                  py-3
                "
              >
                Volver
              </button>
            </>
          )}

        </div>
      </motion.div>
    </div>
  );
}

export function PaymentCancel({ onDone }) {
  const goHome = () => { window.history.replaceState(null, "", "/"); onDone?.(); };
  return (
    <div className="min-h-screen grid place-items-center px-6" data-testid="payment-cancel-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="card-tech rounded-2xl p-10 md:p-14 max-w-xl w-full text-center relative"
      >
        <span className="tag mb-4 inline-flex">Payment · cancelled</span>
        <h1 className="font-display font-light text-4xl md:text-5xl leading-tight text-[rgb(var(--paper))]">
          Pago <span className="italic font-serif">cancelado.</span>
        </h1>
        <p className="mt-4 text-[rgb(var(--paper-dim))]">No se ha cobrado nada. Puedes volver a los packs cuando quieras.</p>
        <button onClick={goHome} className="mt-7 rounded-full bg-[rgb(var(--paper))] text-[rgb(var(--ink))] px-6 py-3 text-sm font-medium" data-testid="cancel-back-btn">
          Volver al estudio
        </button>
      </motion.div>
    </div>
  );
}
